import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, fmtDateTime } from "@/lib/api";
import { EmailBody } from "@/components/EmailBody";
import { TicketAttachment } from "@/components/TicketAttachment";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  LifeBuoy,
  MessageSquare,
  Plus,
  Reply,
  StickyNote,
  Loader2,
  Trash2,
  Maximize2,
  Columns3,
  GripVertical,
  ChevronUp,
  ChevronDown,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";


// ---------------------------------------------------------------------------
// Column catalogue — the user can toggle any of these on/off; selection is
// persisted per-user in localStorage so each engineer gets their own layout.
// Width is a CSS grid-template-column fragment used both for the header row
// and the data rows (keeps alignment pixel-perfect regardless of selection).
// `search` lists the ticket-record fields searched when the user types a
// query — toggling a column ON also starts searching that field.
// ---------------------------------------------------------------------------
const COLUMN_CATALOG = {
  ticket_number:        { label: "#",         width: "80px",              sortable: "ticket_number",   search: ["ticket_number"] },
  status:               { label: "Status",    width: "140px",             sortable: "status",          search: ["status"] },
  owner:                { label: "Owner",     width: "140px",             sortable: "owner_name",      search: ["owner_name"] },
  subject:              { label: "Subject",   width: "minmax(160px,2fr)", sortable: "subject",         search: ["subject"] },
  customer:             { label: "Customer",  width: "minmax(140px,1fr)", sortable: "account_name",    search: ["account_name"] },
  age:                  { label: "Age",       width: "60px",              sortable: "age",             search: [] },
  priority:             { label: "Priority",  width: "90px",              sortable: "priority",        search: ["priority"] },
  vendor_ticket_number: { label: "Vendor #",  width: "120px",             sortable: "vendor_ticket_number", search: ["vendor_ticket_number"] },
  category:             { label: "Category",  width: "120px",             sortable: "category",        search: ["category"] },
  requester_contact:    { label: "Requester", width: "160px",             sortable: "requester_contact_name", search: ["requester_contact_name", "requester_contact_email"] },
  vendor:               { label: "Vendor",    width: "120px",             sortable: "vendor_name",     search: ["vendor_name"] },
  created_at:           { label: "Created",   width: "110px",             sortable: "created_at",      search: [] },
  updated_at:           { label: "Updated",   width: "110px",             sortable: "updated_at",      search: [] },
};
const DEFAULT_VISIBLE_COLUMNS = ["ticket_number", "status", "owner", "subject", "customer", "age", "priority"];
const COLUMN_PREF_KEY = (userId) => `pulse.tickets.columns.v1.${userId || "anon"}`;

function loadVisibleColumns(userId) {
  try {
    const raw = localStorage.getItem(COLUMN_PREF_KEY(userId));
    if (!raw) return DEFAULT_VISIBLE_COLUMNS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_VISIBLE_COLUMNS;
    // Drop any keys we no longer know about and keep order
    const known = parsed.filter((k) => COLUMN_CATALOG[k]);
    return known.length ? known : DEFAULT_VISIBLE_COLUMNS;
  } catch { return DEFAULT_VISIBLE_COLUMNS; }
}

function saveVisibleColumns(userId, cols) {
  try { localStorage.setItem(COLUMN_PREF_KEY(userId), JSON.stringify(cols)); } catch { /* storage full */ }
}

const FALLBACK_STATUSES = [
  { name: "New", color: "violet", is_closed: false },
  { name: "Open", color: "blue", is_closed: false },
  { name: "Waiting on Customer", color: "amber", is_closed: false },
  { name: "Waiting on Internal", color: "orange", is_closed: false },
  { name: "Resolved", color: "emerald", is_closed: true },
  { name: "Closed", color: "slate", is_closed: true },
];

const FALLBACK_PRIORITIES = [
  { name: "Urgent", color: "red", p_level: 1 },
  { name: "High", color: "amber", p_level: 2 },
  { name: "Medium", color: "blue", p_level: 3 },
  { name: "Low", color: "slate", p_level: 4 },
];

const COLOR_TO_CLASS = {
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  red: "bg-red-50 text-red-700 border-red-200",
  slate: "bg-slate-50 text-slate-600 border-slate-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  lime: "bg-lime-50 text-lime-700 border-lime-200",
};

function ageOf(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "—";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  return `${months}mo`;
}

function pill(value, colorMap, testid) {
  if (!value) return <span className="text-slate-400">—</span>;
  const color = colorMap?.[value] || "slate";
  const cls = COLOR_TO_CLASS[color] || COLOR_TO_CLASS.slate;
  return (
    <span
      data-testid={testid}
      className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}
    >
      {value}
    </span>
  );
}


// Render a single list-row cell for one of the catalog columns. Pulled out so
// the visibleCols loop in the main render stays tidy.
function renderCell(key, t, { statusColors, priorityColors }) {
  const muted = <span className="text-slate-400">—</span>;
  switch (key) {
    case "ticket_number":
      return <span key={key} className="font-mono text-xs text-slate-600">#{t.ticket_number || "—"}</span>;
    case "status":
      return <span key={key}>{pill(t.status, statusColors)}</span>;
    case "owner":
      return <span key={key} className="truncate text-slate-700 text-xs">{t.owner_name || <span className="text-slate-400">Unassigned</span>}</span>;
    case "subject":
      return <span key={key} className="truncate text-slate-900 font-medium" title={t.subject}>{t.subject}</span>;
    case "customer":
      return <span key={key} className="truncate text-slate-600 text-xs" title={t.account_name || ""}>{t.account_name || "—"}</span>;
    case "age":
      return <span key={key} className="font-mono text-[11px] text-slate-500">{ageOf(t.created_at)}</span>;
    case "priority":
      return <span key={key}>{pill(t.priority, priorityColors)}</span>;
    case "vendor_ticket_number":
      return <span key={key} className="font-mono text-[11px] text-slate-600 truncate" title={t.vendor_ticket_number || ""}>{t.vendor_ticket_number || muted}</span>;
    case "category":
      return <span key={key} className="truncate text-xs text-slate-600" title={t.category || ""}>{t.category || muted}</span>;
    case "requester_contact":
      return (
        <span key={key} className="truncate text-xs text-slate-700" title={`${t.requester_contact_name || ""}${t.requester_contact_email ? ` · ${t.requester_contact_email}` : ""}`}>
          {t.requester_contact_name || t.requester_contact_email || muted}
        </span>
      );
    case "vendor":
      return <span key={key} className="truncate text-xs text-slate-600" title={t.vendor_name || ""}>{t.vendor_name || muted}</span>;
    case "created_at":
      return <span key={key} className="font-mono text-[11px] text-slate-500">{(t.created_at || "").slice(0, 10) || muted}</span>;
    case "updated_at":
      return <span key={key} className="font-mono text-[11px] text-slate-500">{(t.updated_at || "").slice(0, 10) || muted}</span>;
    default:
      return <span key={key}>{muted}</span>;
  }
}

// ---------------- Column Header ----------------
function ColHeader({ label, field, sortField, sortDir, onSort, className = "" }) {
  const active = sortField === field;
  const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      data-testid={`sort-${field}`}
      className={`flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-900 transition-colors ${className}`}
    >
      {label}
      <Icon className={`h-3 w-3 ${active ? "text-slate-700" : "text-slate-300"}`} />
    </button>
  );
}

// ---------------- Columns Editor (visible-list drag-reorder + hidden add) ----
// Renders inside the "Columns" popover. Two sections:
//   • Visible — ordered list, each row is an HTML5-draggable chip with ↑/↓
//     keyboard-friendly arrows and an × to hide. Drag-drop writes the new
//     order back via `reorderColumn`; the arrows call `moveColumn`.
//   • Available — unchecked columns with a + button to add them to the end.
function ColumnsEditor({ visibleCols, toggleColumn, moveColumn, reorderColumn, resetColumns }) {
  const [dragKey, setDragKey] = useState(null);
  const hidden = Object.keys(COLUMN_CATALOG).filter((k) => !visibleCols.includes(k));

  const onDragStart = (key) => (e) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", key); } catch { /* some browsers */ }
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (beforeKey) => (e) => {
    e.preventDefault();
    const src = dragKey || e.dataTransfer.getData("text/plain");
    if (src && src !== beforeKey) reorderColumn(src, beforeKey);
    setDragKey(null);
  };

  return (
    <div className="p-2" data-testid="columns-editor">
      <div className="px-2 pt-1 pb-2 text-xs font-semibold text-slate-700">
        Visible columns <span className="text-slate-400 font-normal">· drag or use arrows to reorder</span>
      </div>
      <ul className="space-y-1 max-h-80 overflow-y-auto">
        {visibleCols.map((key, idx) => {
          const spec = COLUMN_CATALOG[key];
          if (!spec) return null;
          return (
            <li
              key={key}
              draggable
              onDragStart={onDragStart(key)}
              onDragOver={onDragOver}
              onDrop={onDrop(key)}
              onDragEnd={() => setDragKey(null)}
              className={`group flex items-center gap-1 rounded border bg-white px-1.5 py-1 text-xs transition-colors ${dragKey === key ? "opacity-40 border-blue-300" : "border-slate-200 hover:border-slate-300"}`}
              data-testid={`col-row-${key}`}
            >
              <GripVertical className="h-3.5 w-3.5 text-slate-300 cursor-grab shrink-0" />
              <span className="flex-1 truncate text-slate-700">{spec.label}</span>
              <button
                type="button"
                onClick={() => moveColumn(key, "up")}
                disabled={idx === 0}
                className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400"
                data-testid={`col-up-${key}`}
                title="Move up"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => moveColumn(key, "down")}
                disabled={idx === visibleCols.length - 1}
                className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400"
                data-testid={`col-down-${key}`}
                title="Move down"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => toggleColumn(key)}
                disabled={visibleCols.length <= 1}
                className="p-0.5 text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-slate-400"
                data-testid={`col-hide-${key}`}
                title="Hide column"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          );
        })}
        {/* Drop zone at the end so you can drag a column to the bottom */}
        <li
          onDragOver={onDragOver}
          onDrop={onDrop(null)}
          className={`h-2 rounded ${dragKey ? "bg-slate-100 border border-dashed border-slate-300" : ""}`}
          data-testid="col-drop-end"
        />
      </ul>

      {hidden.length > 0 && (
        <>
          <div className="px-2 pt-3 pb-2 text-xs font-semibold text-slate-700">Available columns</div>
          <ul className="space-y-1">
            {hidden.map((key) => (
              <li key={key} className="flex items-center gap-2 rounded border border-slate-100 bg-slate-50/60 px-1.5 py-1 text-xs">
                <span className="flex-1 truncate text-slate-500">{COLUMN_CATALOG[key].label}</span>
                <button
                  type="button"
                  onClick={() => toggleColumn(key)}
                  className="p-0.5 text-slate-500 hover:text-blue-700"
                  data-testid={`col-add-${key}`}
                  title="Add column"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="flex items-center justify-between border-t border-slate-100 mt-3 pt-2 px-1">
        <button
          type="button"
          className="text-xs text-slate-500 hover:text-slate-900"
          onClick={resetColumns}
          data-testid="col-reset"
        >
          Reset to defaults
        </button>
        <span className="text-[10px] text-slate-400">{visibleCols.length} shown</span>
      </div>
    </div>
  );
}



// ---------------- New Ticket Dialog ----------------
function NewTicketDialog({ open, onOpenChange, accounts, staff, vendors, products, statuses, priorities, onCreated }) {
  const defaultStatus = statuses?.[0]?.name || "New";
  const defaultPriority = priorities?.find((p) => p.name === "Medium")?.name || priorities?.[2]?.name || "Medium";
  const [form, setForm] = useState({
    account_id: "",
    subject: "",
    description: "",
    status: defaultStatus,
    priority: defaultPriority,
    owner_user_id: "",
    vendor_id: "",
    product_id: "",
    vendor_ticket_number: "",
    requester_contact_id: "",
  });
  const [accountSearch, setAccountSearch] = useState("");
  const [accountContacts, setAccountContacts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      account_id: "",
      subject: "",
      description: "",
      status: defaultStatus,
      priority: defaultPriority,
      owner_user_id: "",
      vendor_id: "",
      product_id: "",
      vendor_ticket_number: "",
      requester_contact_id: "",
    });
    setAccountSearch("");
  }, [open]);

  const accountMatches = useMemo(() => {
    const q = accountSearch.trim().toLowerCase();
    const list = accounts || [];
    if (!q) return list.slice(0, 50);
    return list
      .filter((a) => a.name?.toLowerCase().includes(q))
      .slice(0, 50);
  }, [accounts, accountSearch]);

  const selectedAccount = accounts?.find((a) => a.id === form.account_id);
  const selectedOwner = staff?.find((s) => s.user_id === form.owner_user_id);

  // Load account contacts when a customer is picked so we can offer a requester dropdown
  useEffect(() => {
    let cancelled = false;
    if (!form.account_id) { setAccountContacts([]); return; }
    (async () => {
      try {
        const r = await api.get(`/accounts/${form.account_id}/contacts`);
        if (!cancelled) setAccountContacts(r.data || []);
      } catch (e) {
        if (!cancelled) setAccountContacts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [form.account_id]);

  const submit = async () => {
    if (!form.account_id) {
      toast.error("Please select a customer");
      return;
    }
    if (!form.subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        account_id: form.account_id,
        subject: form.subject.trim(),
        description: form.description || null,
        status: form.status,
        priority: form.priority,
        owner_user_id: form.owner_user_id || null,
        owner_name: selectedOwner?.name || null,
        vendor_id: form.vendor_id || null,
        product_id: form.product_id || null,
        vendor_ticket_number: form.vendor_ticket_number || null,
        requester_contact_id: form.requester_contact_id || null,
      };
      const { data } = await api.post("/tickets", payload);
      toast.success(`Ticket #${data.ticket_number || data.id} created`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create ticket");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="new-ticket-dialog">
        <DialogHeader>
          <DialogTitle>New support ticket</DialogTitle>
          <DialogDescription>
            Capture the fault, assign it to a Xentra engineer, and link it to the customer.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          {/* Subject */}
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="t-subject">Subject</Label>
            <Input
              id="t-subject"
              data-testid="new-ticket-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Short summary of the issue"
            />
          </div>

          {/* Customer */}
          <div className="col-span-2 space-y-1.5">
            <Label>Customer</Label>
            <Input
              data-testid="new-ticket-customer-search"
              value={selectedAccount?.name || accountSearch}
              onChange={(e) => {
                setAccountSearch(e.target.value);
                setForm({ ...form, account_id: "" });
              }}
              placeholder="Search accounts…"
            />
            {!form.account_id && accountSearch && (
              <div className="border border-slate-200 rounded-md max-h-40 overflow-y-auto bg-white shadow-sm" data-testid="customer-search-results">
                {accountMatches.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-slate-400">No matches</div>
                ) : (
                  accountMatches.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => {
                        setForm({ ...form, account_id: a.id });
                        setAccountSearch("");
                      }}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50"
                      data-testid={`customer-option-${a.id}`}
                    >
                      {a.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Requester contact (only after a customer is picked) */}
          {form.account_id && (
            <div className="col-span-2 space-y-1.5">
              <Label>Requester contact <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Select
                value={form.requester_contact_id || "__none__"}
                onValueChange={(v) => setForm({ ...form, requester_contact_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger data-testid="new-ticket-requester">
                  <SelectValue placeholder={accountContacts.length ? "Pick the person who reported this" : "No contacts on this account"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  {accountContacts.map((c) => {
                    const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || "Unknown";
                    return (
                      <SelectItem key={c.id} value={c.id}>
                        {name}{c.email ? ` · ${c.email}` : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Status + Priority */}
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="new-ticket-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(statuses || FALLBACK_STATUSES).map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="new-ticket-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(priorities || FALLBACK_PRIORITIES).map((p) => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Owner */}
          <div className="col-span-2 space-y-1.5">
            <Label>Assigned owner</Label>
            <Select
              value={form.owner_user_id || "__none__"}
              onValueChange={(v) => setForm({ ...form, owner_user_id: v === "__none__" ? "" : v })}
            >
              <SelectTrigger data-testid="new-ticket-owner"><SelectValue placeholder="Pick an engineer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {(staff || []).map((u) => (
                  <SelectItem key={u.user_id} value={u.user_id}>
                    {u.name} {u.job_title ? `· ${u.job_title}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Vendor + Product */}
          <div className="space-y-1.5">
            <Label>Vendor</Label>
            <Select
              value={form.vendor_id || "__none__"}
              onValueChange={(v) => setForm({ ...form, vendor_id: v === "__none__" ? "" : v })}
            >
              <SelectTrigger data-testid="new-ticket-vendor"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— none —</SelectItem>
                {(vendors || []).map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Product</Label>
            <Select
              value={form.product_id || "__none__"}
              onValueChange={(v) => setForm({ ...form, product_id: v === "__none__" ? "" : v })}
            >
              <SelectTrigger data-testid="new-ticket-product"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— none —</SelectItem>
                {(products || []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Vendor ticket number */}
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="t-vendor-ticket">Vendor ticket number</Label>
            <Input
              id="t-vendor-ticket"
              data-testid="new-ticket-vendor-number"
              value={form.vendor_ticket_number}
              onChange={(e) => setForm({ ...form, vendor_ticket_number: e.target.value })}
              placeholder="Reference number from the vendor (optional)"
            />
          </div>

          {/* Fault description */}
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="t-desc">Fault description</Label>
            <Textarea
              id="t-desc"
              data-testid="new-ticket-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What's broken, when did it start, who reported it…"
              rows={5}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="new-ticket-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Create ticket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Preview Pane ----------------
function PreviewPane({ ticket, staff, accounts, statuses, priorities, statusColors, priorityColors, isAdmin, canDeleteTickets, onUpdated, onDeleted, onOpenFull }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const threadRef = useRef(null);

  const loadEvents = async () => {
    if (!ticket) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tickets/${ticket.id}/events`);
      setEvents(data);
      // Scroll to bottom after paint
      setTimeout(() => {
        if (threadRef.current) threadRef.current.scrollTop = 0;
      }, 50);
    } catch (e) {
      // Silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line
  }, [ticket?.id]);

  const updateField = async (key, value) => {
    try {
      const payload = { [key]: value };
      if (key === "owner_user_id") {
        const u = staff.find((s) => s.user_id === value);
        payload.owner_name = u?.name || null;
      }
      await api.patch(`/tickets/${ticket.id}`, payload);
      onUpdated?.();
      loadEvents();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const deleteTicket = async () => {
    if (!window.confirm(`Delete ticket #${ticket.ticket_number || ticket.id}? It will be moved to the Admin → Ticket recycle bin (nothing is truly deleted) and can be restored at any time.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/tickets/${ticket.id}`);
      toast.success("Ticket deleted");
      onDeleted?.(ticket.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  if (!ticket) {
    return (
      <Card className="h-full flex items-center justify-center text-slate-400 border-dashed">
        <div className="text-center">
          <MessageSquare className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          <div className="text-sm">Select a ticket to view the thread</div>
        </div>
      </Card>
    );
  }

  const account = accounts?.find((a) => a.id === ticket.account_id);
  const tNum = ticket.ticket_number ? `#${ticket.ticket_number}` : ticket.id;

  return (
    <Card className="h-full flex flex-col overflow-hidden" data-testid="ticket-preview-pane">
      {/* Header */}
      <div className="p-5 border-b border-slate-200 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-mono text-slate-500" data-testid="preview-ticket-number">{tNum}</div>
            <h2 className="font-heading text-lg font-bold text-slate-900 truncate" data-testid="preview-ticket-subject">{ticket.subject}</h2>
            <div className="text-xs text-slate-500 mt-1">
              {account?.name || ticket.account_name || "—"}
              {ticket.vendor_name && <> · Vendor: {ticket.vendor_name}</>}
              {ticket.vendor_ticket_number && <> · Vendor#{ticket.vendor_ticket_number}</>}
              {ticket.product_name && <> · {ticket.product_name}</>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            {pill(ticket.priority, priorityColors, "preview-priority")}
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">Age {ageOf(ticket.created_at)}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onOpenFull?.(ticket.id)}
              data-testid="open-full-view-btn"
              className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
            >
              <Maximize2 className="h-3.5 w-3.5 mr-1" /> Open full view
            </Button>
            {canDeleteTickets && (
              <Button
                size="sm"
                variant="ghost"
                onClick={deleteTicket}
                disabled={deleting}
                data-testid="delete-ticket-btn"
                className="h-7 px-2 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50"
              >
                {deleting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}
                Delete
              </Button>
            )}
          </div>
        </div>

        {/* Inline actions */}
        <div className="flex flex-wrap gap-2 pt-1">
          <Select value={ticket.status} onValueChange={(v) => updateField("status", v)}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid="preview-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>{(statuses || FALLBACK_STATUSES).map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={ticket.priority} onValueChange={(v) => updateField("priority", v)}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="preview-priority-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>{(priorities || FALLBACK_PRIORITIES).map((p) => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select
            value={ticket.owner_user_id || "__none__"}
            onValueChange={(v) => updateField("owner_user_id", v === "__none__" ? null : v)}
          >
            <SelectTrigger className="h-8 w-48 text-xs" data-testid="preview-owner-select">
              <SelectValue placeholder="Unassigned" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Unassigned</SelectItem>
              {(staff || []).map((u) => (
                <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Thread — newest at the top for faster scanning on long email chains.
          The original Fault description moves to the bottom (chronologically
          it's the first message in the chain). */}
      <div ref={threadRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50/40" data-testid="ticket-thread">
        {loading && events.length === 0 && (
          <div className="text-center text-xs text-slate-400 py-6">Loading thread…</div>
        )}

        {[...events].reverse().map((ev) => {
          const isNote = ev.kind === "note" || ev.is_private;
          const isSystem = ev.kind === "system" || ev.kind === "status_change" || ev.kind === "assignment";
          if (ev.kind === "inline_image") return null;
          if (ev.kind === "attachment") {
            return <TicketAttachment key={ev.id} ticketId={ticket.id} meta={ev.meta} createdAt={ev.created_at} fmt={fmtDateTime} />;
          }
          if (isSystem) {
            return (
              <div key={ev.id} className="text-[11px] font-mono text-slate-400 text-center py-1">
                {ev.body} · {fmtDateTime(ev.created_at)}
              </div>
            );
          }
          return (
            <div
              key={ev.id}
              data-testid={`thread-event-${ev.id}`}
              className={`rounded-lg p-3.5 border ${isNote ? "bg-amber-50/60 border-amber-200" : "bg-white border-slate-200"}`}
            >
              <div className="flex items-center justify-between text-[11px] mb-1.5">
                <span className="font-semibold text-slate-700 flex items-center gap-1.5">
                  {isNote ? <StickyNote className="h-3 w-3 text-amber-600" /> : <Reply className="h-3 w-3 text-blue-600" />}
                  {ev.author_name || "Unknown"}
                  {isNote && <span className="text-amber-700 font-mono ml-1">PRIVATE NOTE</span>}
                </span>
                <span className="text-slate-400 font-mono">{fmtDateTime(ev.created_at)}</span>
              </div>
              {ev.body_html
                ? <EmailBody html={ev.body_html} />
                : <div className="text-sm text-slate-700 whitespace-pre-wrap">{ev.body}</div>}
            </div>
          );
        })}

        {ticket.description && (
          <div className="bg-white border border-slate-200 rounded-lg p-3.5" data-testid="preview-thread-description">
            <div className="flex items-center justify-between text-[11px] text-slate-500 font-mono mb-1.5">
              <span>Fault description (original)</span>
              <span>{fmtDateTime(ticket.created_at)}</span>
            </div>
            {ticket.description_html
              ? <EmailBody html={ticket.description_html} />
              : <div className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.description}</div>}
          </div>
        )}

        {!loading && events.length === 0 && !ticket.description && (
          <div className="text-center text-xs text-slate-400 py-6">No updates yet</div>
        )}
      </div>

      {/* Read-only footer — replies happen from full view */}
      <div className="border-t border-slate-200 px-4 py-2.5 bg-slate-50/60 flex items-center justify-between text-xs text-slate-500">
        <span>Preview is read-only. Open full view to reply or add a note.</span>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onOpenFull?.(ticket.id)}
          data-testid="preview-open-full-btn"
          className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
        >
          <Maximize2 className="h-3.5 w-3.5 mr-1" /> Open full view
        </Button>
      </div>
    </Card>
  );
}

// ---------------- Session-scoped filter persistence ----------------
// Filters are stashed in sessionStorage so they survive a round-trip to the
// ticket detail page and back. sessionStorage clears when the browser tab
// closes, so a fresh visit still lands on the default "Open only" view.
const FILTERS_SESSION_KEY = "xentra:tickets:filters:v1";
const DEFAULT_FILTERS = {
  q: "",
  statusFilter: "open",
  priorityFilter: "all",
  ownerFilter: "all",
  customerFilter: "all",
  sortField: "created_at",
  sortDir: "desc",
  selectedId: null,
};

function readSavedFilters() {
  try {
    const raw = sessionStorage.getItem(FILTERS_SESSION_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw);
    // Shallow-merge so any new field added later still gets its default
    return { ...DEFAULT_FILTERS, ...parsed };
  } catch (e) {
    return DEFAULT_FILTERS;
  }
}

// ---------------- Main page ----------------
export default function Tickets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const canDeleteTickets = user?.role === "admin" || user?.role === "support";
  const canSelect = !!user; // any signed-in staff can select (for merge); Edit stays admin-only; Delete is admin+support
  const [tickets, setTickets] = useState([]);
  const [staff, setStaff] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [statuses, setStatuses] = useState(FALLBACK_STATUSES);
  const [priorities, setPriorities] = useState(FALLBACK_PRIORITIES);

  // Hydrate filter + sort + selection state from sessionStorage on mount so a
  // user coming back from `/app/tickets/:id` lands on the same list state.
  const saved = useMemo(readSavedFilters, []);
  const [q, setQ] = useState(saved.q);
  const [statusFilter, setStatusFilter] = useState(saved.statusFilter);
  const [priorityFilter, setPriorityFilter] = useState(saved.priorityFilter);
  const [ownerFilter, setOwnerFilter] = useState(saved.ownerFilter);
  const [customerFilter, setCustomerFilter] = useState(saved.customerFilter);

  const [sortField, setSortField] = useState(saved.sortField);
  const [sortDir, setSortDir] = useState(saved.sortDir);

  const [selectedId, setSelectedId] = useState(saved.selectedId);
  const [openCreate, setOpenCreate] = useState(false);

  // Bulk selection — Set of ticket ids explicitly ticked by the user.
  const [checked, setChecked] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState(null); // 'delete' | 'update' | null

  // Which columns to show on the list. Persisted per-user in localStorage so
  // each engineer can tailor the view (e.g. add "Vendor #" if they live in
  // supplier tickets). Toggling a column on also includes its field(s) in the
  // text search — see COLUMN_CATALOG[k].search.
  const [visibleCols, setVisibleCols] = useState(() => loadVisibleColumns(user?.user_id));
  useEffect(() => {
    // Re-hydrate from storage once the user is known (first paint runs with user=undefined).
    if (user?.user_id) setVisibleCols(loadVisibleColumns(user.user_id));
  }, [user?.user_id]);
  const toggleColumn = (key) => {
    setVisibleCols((cur) => {
      const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
      // Prevent the user from hiding every column — always keep subject.
      const safe = next.length ? next : ["subject"];
      saveVisibleColumns(user?.user_id, safe);
      return safe;
    });
  };
  // Move a visible column up or down the ordered list. Clamps at the ends so
  // a double-click at the top/bottom is a no-op rather than throwing.
  const moveColumn = (key, direction) => {
    setVisibleCols((cur) => {
      const idx = cur.indexOf(key);
      if (idx < 0) return cur;
      const target = idx + (direction === "up" ? -1 : 1);
      if (target < 0 || target >= cur.length) return cur;
      const next = [...cur];
      [next[idx], next[target]] = [next[target], next[idx]];
      saveVisibleColumns(user?.user_id, next);
      return next;
    });
  };
  // Drop `key` immediately before `beforeKey` (or at the end if beforeKey is
  // null). Used by the native HTML5 drag-and-drop on the Columns popover.
  const reorderColumn = (key, beforeKey) => {
    setVisibleCols((cur) => {
      if (!cur.includes(key)) return cur;
      const filtered = cur.filter((k) => k !== key);
      if (beforeKey == null) {
        filtered.push(key);
      } else {
        const i = filtered.indexOf(beforeKey);
        if (i < 0) filtered.push(key);
        else filtered.splice(i, 0, key);
      }
      saveVisibleColumns(user?.user_id, filtered);
      return filtered;
    });
  };
  const resetColumns = () => {
    setVisibleCols(DEFAULT_VISIBLE_COLUMNS);
    saveVisibleColumns(user?.user_id, DEFAULT_VISIBLE_COLUMNS);
  };
  const [bulkForm, setBulkForm] = useState({ status: "", priority: "", owner_user_id: "" });
  const [bulkBusy, setBulkBusy] = useState(false);

  // Persist any filter/sort/selection change back to sessionStorage.
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTERS_SESSION_KEY, JSON.stringify({
        q, statusFilter, priorityFilter, ownerFilter, customerFilter,
        sortField, sortDir, selectedId,
      }));
    } catch (e) { /* storage disabled — best-effort */ }
  }, [q, statusFilter, priorityFilter, ownerFilter, customerFilter, sortField, sortDir, selectedId]);

  const loadAll = async () => {
    try {
      const [tk, st, ac, vd, pr, sts, pri] = await Promise.all([
        api.get("/tickets"),
        api.get("/staff").catch(() => ({ data: [] })),
        api.get("/accounts").catch(() => ({ data: [] })),
        api.get("/vendors").catch(() => ({ data: [] })),
        api.get("/stax-products").catch(() => ({ data: [] })),
        api.get("/config/ticket-statuses").catch(() => ({ data: FALLBACK_STATUSES })),
        api.get("/config/ticket-priorities").catch(() => ({ data: FALLBACK_PRIORITIES })),
      ]);
      setTickets(tk.data);
      setStaff(st.data);
      setAccounts(ac.data);
      setVendors(vd.data);
      setProducts(pr.data);
      setStatuses(sts.data?.length ? sts.data : FALLBACK_STATUSES);
      setPriorities(pri.data?.length ? pri.data : FALLBACK_PRIORITIES);
    } catch (e) {
      toast.error("Failed to load tickets");
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, []);

  // Build color-lookup maps + closed-status set from the configured lists
  const statusColors = useMemo(() => Object.fromEntries(statuses.map((s) => [s.name, s.color || "slate"])), [statuses]);
  const priorityColors = useMemo(() => Object.fromEntries(priorities.map((p) => [p.name, p.color || "slate"])), [priorities]);
  const closedStatusSet = useMemo(() => new Set(statuses.filter((s) => s.is_closed).map((s) => s.name)), [statuses]);
  const isClosed = (status) => closedStatusSet.has(status);

  // Derived list
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let list = tickets.filter((t) => {
      if (statusFilter === "open" && isClosed(t.status)) return false;
      if (statusFilter === "closed" && !isClosed(t.status)) return false;
      if (
        statusFilter !== "all" &&
        statusFilter !== "open" &&
        statusFilter !== "closed" &&
        t.status !== statusFilter
      )
        return false;
      if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
      if (ownerFilter !== "all" && (t.owner_name || "Unassigned") !== ownerFilter) return false;
      if (customerFilter !== "all" && t.account_id !== customerFilter) return false;
      if (ql) {
        // Always include the "core" fields the user considers table-stakes,
        // then extend with whichever extra columns the user has turned on
        // (so toggling "Vendor #" on also makes it searchable).
        const CORE = ["subject", "account_name", "owner_name", "ticket_number", "vendor_ticket_number"];
        const extra = visibleCols.flatMap((k) => (COLUMN_CATALOG[k]?.search || []));
        const fields = Array.from(new Set([...CORE, ...extra]));
        const hay = fields.map((f) => String(t[f] ?? "")).join(" ").toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });

    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const av = (() => {
        if (sortField === "age") return new Date(a.created_at).getTime();
        if (sortField === "ticket_number") return a.ticket_number || 0;
        return (a[sortField] || "").toString().toLowerCase();
      })();
      const bv = (() => {
        if (sortField === "age") return new Date(b.created_at).getTime();
        if (sortField === "ticket_number") return b.ticket_number || 0;
        return (b[sortField] || "").toString().toLowerCase();
      })();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });

    // Age sort: "younger" should be ascending when sortDir=asc → invert
    if (sortField === "age") list.reverse();
    return list;
  }, [tickets, q, statusFilter, priorityFilter, ownerFilter, customerFilter, sortField, sortDir, closedStatusSet, visibleCols]);

  useEffect(() => {
    if (selectedId && !filtered.find((t) => t.id === selectedId) && filtered.length) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  // Derive currently visible ticket: explicit selection > first filtered row
  const selected =
    tickets.find((t) => t.id === selectedId) ||
    (filtered.length ? filtered[0] : null);

  const onSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir(field === "age" || field === "created_at" ? "desc" : "asc");
    }
  };

  const owners = useMemo(() => {
    const set = new Set();
    tickets.forEach((t) => set.add(t.owner_name || "Unassigned"));
    return Array.from(set).sort();
  }, [tickets]);

  const openCount = tickets.filter((t) => !closedStatusSet.has(t.status)).length;

  return (
    <div className="space-y-4" data-testid="tickets-page">
      {/* Page header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Service Desk</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Support tickets</h1>
          <p className="text-slate-500 text-sm mt-1">
            {openCount} open · {tickets.length} total · <span className="text-slate-400">double-click a row to open full view</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="Search subject, customer, vendor #, requester…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-72"
            data-testid="ticket-search"
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-1.5" data-testid="columns-btn">
                <Columns3 className="h-4 w-4" /> Columns
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-0">
              <ColumnsEditor
                visibleCols={visibleCols}
                toggleColumn={toggleColumn}
                moveColumn={moveColumn}
                reorderColumn={reorderColumn}
                resetColumns={resetColumns}
              />
            </PopoverContent>
          </Popover>
          <Button onClick={() => setOpenCreate(true)} data-testid="new-ticket-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New ticket
          </Button>
        </div>
      </div>

      {/* Bulk action toolbar — visible only when rows are ticked */}
      {canSelect && checked.size > 0 && (
        <div className="rounded-md border border-blue-200 bg-blue-50/70 px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-blue-900" data-testid="bulk-selected-count">
            {checked.size} selected
          </span>
          <span className="text-[11px] font-mono text-blue-700">·</span>
          <button
            className="text-xs px-3 py-1 rounded-md border border-indigo-300 bg-white text-indigo-800 hover:bg-indigo-50 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => setBulkAction("merge")}
            disabled={checked.size < 2}
            title={checked.size < 2 ? "Select at least two tickets to merge" : "Merge selected tickets"}
            data-testid="bulk-merge-btn"
          >
            Merge {checked.size}…
          </button>
          {isAdmin && (
            <>
              <button
                className="text-xs px-3 py-1 rounded-md border border-blue-300 bg-white text-blue-800 hover:bg-blue-50 font-medium"
                onClick={() => { setBulkForm({ status: "", priority: "", owner_user_id: "" }); setBulkAction("update"); }}
                data-testid="bulk-edit-btn"
              >
                Edit status / priority / owner…
              </button>
            </>
          )}
          {canDeleteTickets && (
            <button
              className="text-xs px-3 py-1 rounded-md border border-red-300 bg-white text-red-700 hover:bg-red-50 font-medium"
              onClick={() => setBulkAction("delete")}
              data-testid="bulk-delete-btn"
            >
              Delete {checked.size}…
            </button>
          )}
          <button
            className="text-xs text-slate-600 hover:text-slate-900 ml-auto"
            onClick={() => setChecked(new Set())}
            data-testid="bulk-clear-btn"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Filter bar */}
      <Card className="border-slate-200 p-3 flex flex-wrap items-center gap-2.5">
        <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mr-1">Filter</span>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open only</SelectItem>
            <SelectItem value="closed">Resolved / Closed</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {priorities.map((p) => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={ownerFilter} onValueChange={setOwnerFilter}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="filter-owner"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All owners</SelectItem>
            {owners.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={customerFilter} onValueChange={setCustomerFilter}>
          <SelectTrigger className="h-8 w-52 text-xs" data-testid="filter-customer"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-80 overflow-y-auto">
            <SelectItem value="all">All customers</SelectItem>
            {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>

        {(statusFilter !== "open" || priorityFilter !== "all" || ownerFilter !== "all" || customerFilter !== "all" || q) && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={() => {
              setStatusFilter("open");
              setPriorityFilter("all");
              setOwnerFilter("all");
              setCustomerFilter("all");
              setQ("");
            }}
            data-testid="clear-filters"
          >
            Clear
          </Button>
        )}
      </Card>

      {/* Split pane */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 min-h-[640px]">
        {/* Left list */}
        <Card className="lg:col-span-3 border-slate-200 overflow-hidden flex flex-col">
          {/* Column headers */}
          {(() => {
            const gridTemplate = `36px ${visibleCols.map((k) => COLUMN_CATALOG[k].width).join(" ")}`;
            return (
          <>
          <div className="grid gap-3 px-4 py-2.5 border-b border-slate-200 bg-slate-50 sticky top-0 z-10" style={{ gridTemplateColumns: gridTemplate }}>
            {canSelect ? (
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 accent-blue-600 cursor-pointer justify-self-center"
                data-testid="bulk-select-all"
                aria-label="Select all filtered tickets"
                checked={filtered.length > 0 && filtered.every((t) => checked.has(t.id))}
                onChange={(e) => {
                  if (e.target.checked) {
                    const next = new Set(checked);
                    filtered.forEach((t) => next.add(t.id));
                    setChecked(next);
                  } else {
                    const filteredIds = new Set(filtered.map((t) => t.id));
                    setChecked(new Set([...checked].filter((id) => !filteredIds.has(id))));
                  }
                }}
              />
            ) : <span />}
            {visibleCols.map((key) => {
              const spec = COLUMN_CATALOG[key];
              return (
                <ColHeader
                  key={key}
                  label={spec.label}
                  field={spec.sortable}
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={onSort}
                />
              );
            })}
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-slate-100" data-testid="ticket-list">
            {filtered.length === 0 ? (
              <div className="p-10 text-center">
                <LifeBuoy className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <div className="font-heading text-sm text-slate-600">No tickets match</div>
                <div className="text-xs text-slate-400 mt-1">Try changing the filters or clearing search.</div>
              </div>
            ) : (
              filtered.map((t) => {
                const active = t.id === selectedId;
                const ticked = checked.has(t.id);
                return (
                  <div
                    key={t.id}
                    data-testid={`ticket-row-${t.id}`}
                    onClick={() => setSelectedId(t.id)}
                    onDoubleClick={() => navigate(`/app/tickets/${t.id}`)}
                    className={`w-full grid gap-3 px-4 py-3 text-left text-sm items-center transition-colors hover:bg-blue-50/40 cursor-pointer ${active ? "bg-blue-50/80 border-l-2 border-blue-500" : ""}`}
                    style={{ gridTemplateColumns: gridTemplate }}
                  >
                    {canSelect ? (
                      <input
                        type="checkbox"
                        aria-label={`Select ticket #${t.ticket_number || t.id}`}
                        data-testid={`bulk-select-${t.id}`}
                        className="h-4 w-4 rounded border-slate-300 accent-blue-600 cursor-pointer justify-self-center"
                        checked={ticked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const next = new Set(checked);
                          if (e.target.checked) next.add(t.id); else next.delete(t.id);
                          setChecked(next);
                        }}
                      />
                    ) : <span />}
                    {visibleCols.map((key) => renderCell(key, t, { statusColors, priorityColors }))}
                  </div>
                );
              })
            )}
          </div>
          </>);
          })()}
        </Card>

        {/* Right preview */}
        <div className="lg:col-span-2 min-h-[640px]">
          <PreviewPane
            ticket={selected}
            staff={staff}
            accounts={accounts}
            statuses={statuses}
            priorities={priorities}
            statusColors={statusColors}
            priorityColors={priorityColors}
            isAdmin={isAdmin}
            canDeleteTickets={canDeleteTickets}
            onUpdated={loadAll}
            onOpenFull={(id) => navigate(`/app/tickets/${id}`)}
            onDeleted={(deletedId) => {
              setTickets((prev) => prev.filter((t) => t.id !== deletedId));
              if (selectedId === deletedId) setSelectedId(null);
            }}
          />
        </div>
      </div>

      <NewTicketDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        accounts={accounts}
        staff={staff}
        vendors={vendors}
        products={products}
        statuses={statuses}
        priorities={priorities}
        onCreated={async (t) => {
          // Optimistically insert so the derived `filtered` list already
          // contains the new ticket before we select it — prevents the
          // "selectedId reset to filtered[0]" race from the refresh effect.
          setTickets((prev) => [t, ...prev.filter((x) => x.id !== t.id)]);
          setSelectedId(t.id);
          await loadAll();
        }}
      />

      {/* Bulk delete confirmation */}
      <Dialog open={bulkAction === "delete"} onOpenChange={(o) => !o && setBulkAction(null)}>
        <DialogContent data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete {checked.size} ticket{checked.size === 1 ? "" : "s"}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-slate-700 py-2">
            <p>Selected tickets will be soft-deleted. They stay in the database for audit and can be restored from Admin → Deleted tickets.</p>
            <p className="text-xs text-slate-500">Tickets already deleted are silently skipped.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAction(null)} data-testid="bulk-cancel-btn">Cancel</Button>
            <Button
              variant="destructive"
              disabled={bulkBusy}
              onClick={async () => {
                setBulkBusy(true);
                try {
                  const ids = Array.from(checked);
                  const r = await api.post("/tickets/bulk", { ticket_ids: ids, action: "delete" });
                  toast.success(`Deleted ${r.data.deleted} · skipped ${r.data.skipped}${r.data.errors?.length ? ` · ${r.data.errors.length} error${r.data.errors.length === 1 ? "" : "s"}` : ""}`);
                  setTickets((prev) => prev.filter((t) => !checked.has(t.id)));
                  setChecked(new Set());
                  setBulkAction(null);
                  await loadAll();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Bulk delete failed");
                } finally { setBulkBusy(false); }
              }}
              data-testid="bulk-delete-confirm-btn"
            >
              {bulkBusy ? "Deleting…" : `Delete ${checked.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk edit dialog */}
      <Dialog open={bulkAction === "update"} onOpenChange={(o) => !o && setBulkAction(null)}>
        <DialogContent data-testid="bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit {checked.size} ticket{checked.size === 1 ? "" : "s"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 text-sm">
            <p className="text-xs text-slate-500">Only the fields you set below will be updated across all selected tickets. Leave a field on "— keep as is —" to skip it.</p>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Status</label>
              <Select value={bulkForm.status || "__keep__"} onValueChange={(v) => setBulkForm((f) => ({ ...f, status: v === "__keep__" ? "" : v }))}>
                <SelectTrigger className="mt-1" data-testid="bulk-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__keep__">— keep as is —</SelectItem>
                  {statuses.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Priority</label>
              <Select value={bulkForm.priority || "__keep__"} onValueChange={(v) => setBulkForm((f) => ({ ...f, priority: v === "__keep__" ? "" : v }))}>
                <SelectTrigger className="mt-1" data-testid="bulk-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__keep__">— keep as is —</SelectItem>
                  {priorities.map((p) => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Owner</label>
              <Select value={bulkForm.owner_user_id || "__keep__"} onValueChange={(v) => setBulkForm((f) => ({ ...f, owner_user_id: v === "__keep__" ? "" : v }))}>
                <SelectTrigger className="mt-1" data-testid="bulk-owner-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__keep__">— keep as is —</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {(staff || []).map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAction(null)} data-testid="bulk-edit-cancel-btn">Cancel</Button>
            <Button
              disabled={bulkBusy || (!bulkForm.status && !bulkForm.priority && !bulkForm.owner_user_id)}
              onClick={async () => {
                setBulkBusy(true);
                try {
                  const body = { ticket_ids: Array.from(checked), action: "update" };
                  if (bulkForm.status) body.status = bulkForm.status;
                  if (bulkForm.priority) body.priority = bulkForm.priority;
                  if (bulkForm.owner_user_id) body.owner_user_id = bulkForm.owner_user_id;
                  const r = await api.post("/tickets/bulk", body);
                  toast.success(`Updated ${r.data.updated} · skipped ${r.data.skipped}${r.data.errors?.length ? ` · ${r.data.errors.length} error${r.data.errors.length === 1 ? "" : "s"}` : ""}`);
                  setChecked(new Set());
                  setBulkAction(null);
                  await loadAll();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Bulk update failed");
                } finally { setBulkBusy(false); }
              }}
              data-testid="bulk-edit-apply-btn"
            >
              {bulkBusy ? "Applying…" : "Apply to " + checked.size}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge confirmation */}
      <Dialog open={bulkAction === "merge"} onOpenChange={(o) => !o && setBulkAction(null)}>
        <DialogContent data-testid="bulk-merge-dialog">
          <DialogHeader>
            <DialogTitle>Merge {checked.size} tickets</DialogTitle>
          </DialogHeader>
          {(() => {
            const sel = tickets.filter((t) => checked.has(t.id));
            const numOf = (t) => (typeof t.ticket_number === "number" ? t.ticket_number : Number.MAX_SAFE_INTEGER);
            const survivor = [...sel].sort((a, b) => numOf(a) - numOf(b) || String(a.created_at).localeCompare(String(b.created_at)))[0];
            const others = sel.filter((t) => t.id !== survivor?.id);
            return (
              <div className="space-y-3 py-1 text-sm">
                <p className="text-slate-700">
                  The <b>oldest</b> ticket stays open and receives all updates. The others are merged in and
                  soft-deleted (recoverable from Admin → Deleted tickets, fully audited).
                </p>
                {survivor && (
                  <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2" data-testid="merge-survivor">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-700">Stays open (survivor)</div>
                    <div className="font-medium text-slate-900">#{survivor.ticket_number} — {survivor.subject}</div>
                    <div className="text-xs text-slate-500">{survivor.account_name || "—"} · currently {survivor.status}</div>
                  </div>
                )}
                <div className="rounded-md border border-rose-200 bg-rose-50/60 px-3 py-2">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-rose-700">Merged in & removed</div>
                  <ul className="mt-1 space-y-0.5" data-testid="merge-others">
                    {others.map((t) => (
                      <li key={t.id} className="text-xs text-slate-700">#{t.ticket_number} — {t.subject}</li>
                    ))}
                  </ul>
                </div>
                <p className="text-[11px] text-slate-500">If the survivor is Closed/Resolved it will be re-opened to “New”.</p>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAction(null)} data-testid="merge-cancel-btn">Cancel</Button>
            <Button
              disabled={bulkBusy || checked.size < 2}
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={async () => {
                setBulkBusy(true);
                try {
                  const r = await api.post("/tickets/merge", { ticket_ids: Array.from(checked) });
                  toast.success(`Merged into #${r.data.primary_number}${r.data.reopened ? " · re-opened" : ""}`);
                  setChecked(new Set());
                  setBulkAction(null);
                  setSelectedId(r.data.primary_id);
                  await loadAll();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Merge failed");
                } finally { setBulkBusy(false); }
              }}
              data-testid="merge-confirm-btn"
            >
              {bulkBusy ? "Merging…" : "Merge tickets"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
